import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { authStore } from '../../stores/auth.ts'
import { config } from '../../config/index.ts'

@customElement('register-page')
export class RegisterPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    .container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }

    .card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      width: 100%;
      max-width: 420px;
      padding: 40px;
    }

    /* Registration Closed Styles */
    .closed-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 24px;
      padding: 24px 0;
    }

    .lock-icon-wrapper {
      position: relative;
      width: 80px;
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
      border-radius: 50%;
    }

    .lock-icon {
      width: 40px;
      height: 40px;
      color: #d97706;
    }

    .rotating-border {
      position: absolute;
      inset: -8px;
      border-radius: 50%;
      border: 2px dashed #fbbf24;
      opacity: 0.5;
      animation: rotate 20s linear infinite;
    }

    @keyframes rotate {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .closed-title {
      font-size: 24px;
      font-weight: 600;
      color: #1e293b;
      margin: 0;
      text-align: center;
    }

    .closed-description {
      font-size: 14px;
      color: #64748b;
      text-align: center;
      max-width: 400px;
      margin: 0;
      line-height: 1.6;
    }

    .info-cards {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;
      margin-top: 8px;
    }

    .info-card {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      background: #f8fafc;
      transition: all 0.2s ease;
    }

    .info-card:hover {
      border-color: #cbd5e1;
      background: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .info-icon-wrapper {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
    }

    .info-icon-wrapper.blue {
      background: #dbeafe;
    }

    .info-icon-wrapper.purple {
      background: #e9d5ff;
    }

    .info-icon {
      width: 16px;
      height: 16px;
    }

    .info-icon.blue {
      color: #2563eb;
    }

    .info-icon.purple {
      color: #9333ea;
    }

    .info-content {
      flex: 1;
    }

    .info-title {
      font-size: 14px;
      font-weight: 500;
      color: #1e293b;
      margin: 0 0 4px 0;
    }

    .info-text {
      font-size: 12px;
      color: #64748b;
      margin: 0;
      line-height: 1.5;
    }

    .divider-wrapper {
      position: relative;
      width: 100%;
      margin: 16px 0;
    }

    .divider-line {
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      background: #e2e8f0;
    }

    .divider-text {
      position: relative;
      display: inline-block;
      padding: 0 16px;
      background: white;
      font-size: 12px;
      color: #94a3b8;
      left: 50%;
      transform: translateX(-50%);
    }

    .back-button {
      width: 100%;
      padding: 12px 24px;
      background: linear-gradient(135deg, #3b82f6, #6366f1);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }

    .back-button:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
    }

    .back-button-text {
      display: inline-block;
      transition: transform 0.2s ease;
    }

    .back-button:hover .back-button-text {
      transform: translateX(-4px);
    }

    .back-hint {
      text-align: center;
      font-size: 12px;
      color: #94a3b8;
      margin: 0;
    }

    .logo {
      text-align: center;
      margin-bottom: 32px;
    }

    .logo-icon {
      width: 64px;
      height: 64px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 28px;
      font-weight: bold;
      color: white;
      margin-bottom: 16px;
    }

    .logo h1 {
      font-size: 24px;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }

    .logo p {
      color: #64748b;
      margin: 8px 0 0;
      font-size: 14px;
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-label {
      display: block;
      font-size: 14px;
      font-weight: 500;
      color: #374151;
      margin-bottom: 6px;
    }

    .form-input {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-size: 14px;
      transition: all 0.2s;
      box-sizing: border-box;
    }

    .form-input:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    .form-input.error {
      border-color: #ef4444;
    }

    .error-message {
      color: #ef4444;
      font-size: 12px;
      margin-top: 4px;
    }

    .password-strength {
      margin-top: 8px;
    }

    .strength-bar {
      display: flex;
      gap: 4px;
      margin-bottom: 4px;
    }

    .strength-segment {
      height: 4px;
      flex: 1;
      background: #e2e8f0;
      border-radius: 2px;
      transition: background 0.3s;
    }

    .strength-segment.active.weak {
      background: #ef4444;
    }

    .strength-segment.active.fair {
      background: #f59e0b;
    }

    .strength-segment.active.good {
      background: #10b981;
    }

    .strength-segment.active.strong {
      background: #059669;
    }

    .strength-text {
      font-size: 12px;
      color: #64748b;
    }

    .submit-btn {
      width: 100%;
      padding: 12px 24px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      margin-top: 8px;
    }

    .submit-btn:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }

    .submit-btn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .login-link {
      text-align: center;
      margin-top: 24px;
      font-size: 14px;
      color: #64748b;
    }

    .login-link a {
      color: #667eea;
      text-decoration: none;
      font-weight: 500;
      cursor: pointer;
    }

    .login-link a:hover {
      text-decoration: underline;
    }

    .terms {
      font-size: 12px;
      color: #64748b;
      text-align: center;
      margin-top: 16px;
    }

    .terms a {
      color: #667eea;
      text-decoration: none;
    }

    .social-login {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
      margin-top: 20px;
      margin-bottom: 24px;
    }

    .social-btn {
      height: 44px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      background: #f8fafc;
      cursor: pointer;
      transition: all 0.2s ease;
      color: #0f172a;
      text-decoration: none;
    }

    .social-btn:hover {
      background: #fff;
      border-color: #cbd5e1;
      box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
      transform: translateY(-1px);
    }

    .divider {
      display: flex;
      align-items: center;
      margin: 18px 0;
      color: #94a3b8;
      font-size: 12px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .divider::before,
    .divider::after {
      content: '';
      flex: 1;
      height: 1px;
      background: linear-gradient(to right, transparent, #e2e8f0, transparent);
    }

    .divider span {
      padding: 0 12px;
      font-weight: 600;
    }
  `

  @state()
  private name = ''

  @state()
  private email = ''

  @state()
  private password = ''

  @state()
  private confirmPassword = ''

  @state()
  private loading = false

  @state()
  private errors: Record<string, string> = {}

  // Read registration enabled flag from environment variable (defaults to false)
  private get registrationEnabled(): boolean {
    return import.meta.env.VITE_ENABLE_REGISTRATION === 'true'
  }

  private readonly SOCIAL_LINKS = {
    github: 'https://github.com/halolight/halolight-lit',
    google: 'https://halolight-docs.h7ml.cn',
    wechat: 'https://github.com/halolight',
  }

  private getPasswordStrength(): { level: number; text: string; class: string } {
    const password = this.password
    if (!password) return { level: 0, text: '', class: '' }

    let score = 0
    if (password.length >= 8) score++
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
    if (/\d/.test(password)) score++
    if (/[^a-zA-Z0-9]/.test(password)) score++
    if (password.length >= 12) score++

    if (score <= 1) return { level: 1, text: '弱', class: 'weak' }
    if (score === 2) return { level: 2, text: '一般', class: 'fair' }
    if (score === 3) return { level: 3, text: '良好', class: 'good' }
    return { level: 4, text: '强', class: 'strong' }
  }

  private validate(): boolean {
    const errors: Record<string, string> = {}

    if (!this.name.trim()) {
      errors.name = '请输入姓名'
    }

    if (!this.email.trim()) {
      errors.email = '请输入邮箱'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
      errors.email = '邮箱格式不正确'
    }

    if (!this.password) {
      errors.password = '请输入密码'
    } else if (this.password.length < 6) {
      errors.password = '密码至少 6 位'
    }

    if (this.password !== this.confirmPassword) {
      errors.confirmPassword = '两次密码不一致'
    }

    this.errors = errors
    return Object.keys(errors).length === 0
  }

  private async handleSubmit(e: Event) {
    e.preventDefault()

    if (!this.validate()) return

    this.loading = true
    const success = await authStore.register(this.name, this.email, this.password)
    this.loading = false

    if (success) {
      this.dispatchEvent(new CustomEvent('navigate', {
        detail: { route: 'dashboard' },
        bubbles: true,
        composed: true,
      }))
    }
  }

  private handleNavigate(route: string) {
    this.dispatchEvent(new CustomEvent('navigate', {
      detail: { route },
      bubbles: true,
      composed: true,
    }))
  }

  private renderLockIcon() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    `
  }

  private renderMailIcon() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect width="20" height="16" x="2" y="4" rx="2"/>
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
      </svg>
    `
  }

  private renderPhoneIcon() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    `
  }

  private renderClosedUI() {
    return html`
      <div class="closed-container">
        <!-- Lock Icon with Rotating Border -->
        <div class="lock-icon-wrapper">
          <div class="lock-icon">${this.renderLockIcon()}</div>
          <div class="rotating-border"></div>
        </div>

        <!-- Title and Description -->
        <div>
          <h2 class="closed-title">注册已关闭</h2>
          <p class="closed-description">系统管理员已暂时关闭新用户注册功能</p>
        </div>

        <!-- Info Cards -->
        <div class="info-cards">
          <div class="info-card">
            <div class="info-icon-wrapper blue">
              <div class="info-icon blue">${this.renderMailIcon()}</div>
            </div>
            <div class="info-content">
              <p class="info-title">联系管理员</p>
              <p class="info-text">如需创建账号，请通过邮件联系系统管理员</p>
            </div>
          </div>

          <div class="info-card">
            <div class="info-icon-wrapper purple">
              <div class="info-icon purple">${this.renderPhoneIcon()}</div>
            </div>
            <div class="info-content">
              <p class="info-title">已有账号？</p>
              <p class="info-text">如果您已有账号，请直接登录使用系统功能</p>
            </div>
          </div>
        </div>

        <!-- Divider -->
        <div class="divider-wrapper">
          <div class="divider-line"></div>
          <span class="divider-text">感谢您的理解</span>
        </div>

        <!-- Back to Login Button -->
        <button class="back-button" @click=${() => this.handleNavigate('login')}>
          <span class="back-button-text">← 返回登录</span>
        </button>
        <p class="back-hint">使用现有账号登录系统</p>
      </div>
    `
  }

  render() {
    const strength = this.getPasswordStrength()

    return html`
      <div class="container">
        <div class="card">
          <div class="logo">
            <div class="logo-icon">H</div>
            <h1>${config.brandName}</h1>
            <p>创建您的账户</p>
          </div>

          ${!this.registrationEnabled
            ? this.renderClosedUI()
            : html`
          <!-- 社交注册按钮 -->
          <div class="social-login">
            <a class="social-btn" href=${this.SOCIAL_LINKS.github} target="_blank" rel="noopener noreferrer" title="GitHub 登录">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
            </a>
            <a class="social-btn" href=${this.SOCIAL_LINKS.google} target="_blank" rel="noopener noreferrer" title="Google 登录">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="4"></circle><line x1="21.17" y1="8" x2="12" y2="8"></line><line x1="3.95" y1="6.06" x2="8.54" y2="14"></line><line x1="10.88" y1="21.94" x2="15.46" y2="14"></line></svg>
            </a>
            <a class="social-btn" href=${this.SOCIAL_LINKS.wechat} target="_blank" rel="noopener noreferrer" title="微信登录">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/></svg>
            </a>
          </div>

          <div class="divider"><span>或使用邮箱注册</span></div>

          <form @submit=${this.handleSubmit}>
            <div class="form-group">
              <label class="form-label">姓名</label>
              <input
                type="text"
                class="form-input ${this.errors.name ? 'error' : ''}"
                placeholder="请输入姓名"
                .value=${this.name}
                @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)}
              />
              ${this.errors.name ? html`<div class="error-message">${this.errors.name}</div>` : ''}
            </div>

            <div class="form-group">
              <label class="form-label">邮箱</label>
              <input
                type="email"
                class="form-input ${this.errors.email ? 'error' : ''}"
                placeholder="请输入邮箱"
                .value=${this.email}
                @input=${(e: Event) => (this.email = (e.target as HTMLInputElement).value)}
              />
              ${this.errors.email ? html`<div class="error-message">${this.errors.email}</div>` : ''}
            </div>

            <div class="form-group">
              <label class="form-label">密码</label>
              <input
                type="password"
                class="form-input ${this.errors.password ? 'error' : ''}"
                placeholder="请输入密码"
                .value=${this.password}
                @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)}
              />
              ${this.errors.password ? html`<div class="error-message">${this.errors.password}</div>` : ''}
              ${this.password
                ? html`
                    <div class="password-strength">
                      <div class="strength-bar">
                        ${[1, 2, 3, 4].map(
                          (i) => html`
                            <div class="strength-segment ${i <= strength.level ? `active ${strength.class}` : ''}"></div>
                          `,
                        )}
                      </div>
                      <div class="strength-text">密码强度: ${strength.text}</div>
                    </div>
                  `
                : ''}
            </div>

            <div class="form-group">
              <label class="form-label">确认密码</label>
              <input
                type="password"
                class="form-input ${this.errors.confirmPassword ? 'error' : ''}"
                placeholder="请再次输入密码"
                .value=${this.confirmPassword}
                @input=${(e: Event) => (this.confirmPassword = (e.target as HTMLInputElement).value)}
              />
              ${this.errors.confirmPassword
                ? html`<div class="error-message">${this.errors.confirmPassword}</div>`
                : ''}
            </div>

            <button type="submit" class="submit-btn" ?disabled=${this.loading}>
              ${this.loading ? '注册中...' : '注册'}
            </button>
          </form>

          <div class="terms">
            注册即表示您同意我们的 <a href="#">服务条款</a> 和 <a href="#">隐私政策</a>
          </div>

          <div class="login-link">
            已有账户? <a @click=${() => this.handleNavigate('login')}>立即登录</a>
          </div>
          `}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'register-page': RegisterPage
  }
}
